var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { AlertTriangleIcon, CheckIcon, InfoIcon, XIcon } from '../../icon';
import { rootShouldForwardProp, styled } from '../../styled';
import { statusIconComponentName, statusIconClasses, getStatusIconUtilityClass, } from './status-icon-classes';
import clsx from '../../utils/clsx';
import { useMergedClasses } from '../../styles/classes';
export var getStatusIconStyles = function (theme, bgColor, fgColor) { return ({
    background: theme.tokens[bgColor],
    color: fgColor ? theme.tokens[fgColor] : theme.tokens.color_fg_inverse_static,
}); };
var defaultForegroundColorMap = {
    default: 'color_fg_on_state_neutral_subtle',
    danger: 'color_fg_on_state_danger_subtle',
    warning: 'color_fg_on_state_caution_subtle',
    info: 'color_fg_on_state_info_subtle',
    success: 'color_fg_on_state_success_subtle',
};
var defaultColorMap = {
    default: 'color_bg_state_neutral_subtle',
    danger: 'color_bg_state_danger_subtle',
    warning: 'color_bg_state_caution_subtle',
    info: 'color_bg_state_info_subtle',
    success: 'color_bg_state_success_subtle',
};
var filledColorMap = {
    default: 'color_bg_state_neutral',
    danger: 'color_bg_state_danger',
    warning: 'color_bg_state_caution',
    info: 'color_bg_state_info',
    success: 'color_bg_state_success',
};
var Root = styled('span', {
    shouldForwardProp: function (prop) { return rootShouldForwardProp(prop) && prop !== 'color' && prop !== 'variant'; },
    name: statusIconComponentName,
    slot: 'Root',
})(function (_a) {
    var theme = _a.theme, ownerState = _a.ownerState;
    var iconClass;
    var defaultVariant = {
        backgroundColor: theme.tokens.color_bg_state_neutral,
        color: theme.tokens.color_fg_inverse_static,
    };
    var showDefaultVariant = ownerState.variant === 'default' && defaultVariant;
    if (ownerState.variant === 'filled') {
        var filledColorName = ownerState.color && filledColorMap[ownerState.color];
        iconClass = filledColorName && getStatusIconStyles(theme, filledColorName);
    }
    else {
        var colorName = ownerState.color && defaultColorMap[ownerState.color];
        var fgColorName = ownerState.color && defaultForegroundColorMap[ownerState.color];
        iconClass = colorName && fgColorName && getStatusIconStyles(theme, colorName, fgColorName);
    }
    return __assign(__assign(__assign({}, showDefaultVariant), iconClass), { height: 20, width: 20, fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' });
});
var statusIconMap = {
    danger: XIcon,
    default: InfoIcon,
    info: InfoIcon,
    success: CheckIcon,
    warning: AlertTriangleIcon,
};
var labelMap = {
    danger: 'Error',
    default: 'Information',
    info: 'Information',
    success: 'Success',
    warning: 'Warning',
};
export var StatusIcon = React.forwardRef(function (props, ref) {
    var _a = props.color, color = _a === void 0 ? 'default' : _a, _b = props.variant, variant = _b === void 0 ? 'default' : _b, children = props.children, label = props.label, className = props.className, propClasses = props.classes, IconProp = props.icon, rootProps = __rest(props, ["color", "variant", "children", "label", "className", "classes", "icon"]);
    var classes = useMergedClasses(statusIconClasses, getStatusIconUtilityClass, propClasses);
    var Icon = IconProp || statusIconMap[color];
    var ariaLabel = label || labelMap[color];
    return (React.createElement(Root, __assign({ ref: ref, ownerState: { variant: variant, color: color }, className: clsx(classes.root, className) }, rootProps),
        React.createElement(Icon, { className: classes.icon, "aria-label": ariaLabel, role: "img", size: "1em" })));
});
